import {DateTime} from 'luxon';
import {downloadFile} from './utils';

/**
 * Serializes rows to semicolon-separated CSV with a UTF-8 BOM, so Danish Excel opens the file
 * directly with correct columns and diacritics. Cells containing the separator, quotes, or line
 * breaks are quoted per RFC 4180; `null` renders as an empty cell.
 */
export function buildCsv(rows: ReadonlyArray<ReadonlyArray<string | null>>): string {
    return '\uFEFF' + rows.map(row => row.map(escapeCsvCell).join(';')).join('\r\n');
}

/**
 * Hands the rows to the browser as `<fileNamePrefix>-<yyyy-MM-dd>.csv`, so every CSV export names
 * and types its file the same way.
 */
export function downloadCsv(
    fileNamePrefix: string,
    rows: ReadonlyArray<ReadonlyArray<string | null>>,
): void {
    const today = DateTime.now().toFormat('yyyy-MM-dd');
    downloadFile(
        `${fileNamePrefix}-${today}.csv`,
        new Blob([buildCsv(rows)], {type: 'text/csv;charset=utf-8'}),
    );
}

/**
 * A cell opening with one of these is evaluated as a formula by Excel and LibreOffice, so a
 * backend-supplied value could run `=WEBSERVICE(...)` on the machine that opens the export.
 */
const FORMULA_LEADING_CHARACTERS = ['=', '+', '-', '@', '\t', '\r'];

function escapeCsvCell(cell: string | null): string {
    const value = cell ?? '';
    // The apostrophe tells a spreadsheet to treat the rest as literal text, and it is stripped
    // from the displayed value - so the guard costs nothing for the ordinary cells it never hits.
    const guarded = FORMULA_LEADING_CHARACTERS.includes(value.charAt(0)) ? `'${value}` : value;
    // The comma is quoted even though it is not the separator: Excel splits on the machine's list
    // separator, so on a comma-locale a bare comma would re-split the row and could expose the
    // text after it as a fresh formula-leading field.
    return /[",;\r\n]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
}
